import PropTypes from 'prop-types';
import { useState, useMemo } from 'react';
import { Link } from 'react-router';
import useAnalystStore, { analystActions } from '@/stores/analystStore';
import LCAResultCard from './LCAResultCard';
import TEAResultCard from './TEAResultCard';
import ComparisonResultCard from './ComparisonResultCard';
import SensitivityResultCard from './SensitivityResultCard';
import SweepResultCard from './SweepResultCard';
import AssumptionsCard from './AssumptionsCard';
import NodeParamEditor from './NodeParamEditor';
import MethodologyCard from './MethodologyCard';
import PathwayDiagram from './PathwayDiagram';
import CustomContentCard from './CustomContentCard';

// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Index view (grouped by pathway)
// ---------------------------------------------------------------------------

const PATHWAY_INDEPENDENT_TYPES = new Set(['get_comparison_results']);

function ResultRow({ item, tabs, projectId }) {
  const isOpen = tabs.includes(item.id);
  const pathwayId = item.data?.pathway_id;
  const analysisUrl = projectId && pathwayId
    ? `/projects/${projectId}/analysis/pathways/${pathwayId}`
    : null;

  // Short label: strip the " — PathwayName" suffix since it's shown in the group header.
  const shortLabel = (() => {
    const dashIdx = item.label.indexOf(' — ');
    return dashIdx !== -1 ? item.label.slice(0, dashIdx) : item.label;
  })();

  return (
    <div className="flex items-center gap-2 py-1.5 px-3 border-b border-stone-50 last:border-0 group/row">
      <button
        className="flex-1 text-left text-sm text-slate-600 hover:text-slate-900 truncate"
        onClick={() => analystActions.openTab(item.id)}
      >
        {shortLabel}
        {isOpen && <span className="ml-1.5 text-xs text-slate-400 font-medium">· open</span>}
      </button>
      <div className="flex items-center gap-1.5 shrink-0 opacity-0 group-hover/row:opacity-100 transition-opacity">
        <button
          className="text-xs text-slate-400 hover:text-slate-700 px-1.5 py-0.5 rounded hover:bg-stone-100"
          onClick={() => analystActions.openTab(item.id)}
          title="Open in workspace"
        >
          Open
        </button>
        {analysisUrl && (
          <Link
            to={analysisUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-slate-400 hover:text-slate-700 px-1.5 py-0.5 rounded hover:bg-stone-100"
            title="View analysis in Builder"
            onClick={e => e.stopPropagation()}
          >
            Builder ↗
          </Link>
        )}
      </div>
    </div>
  );
}

ResultRow.propTypes = {
  item: PropTypes.object.isRequired,
  tabs: PropTypes.array.isRequired,
  projectId: PropTypes.string,
};

function IndexView({ history, tabs, projectId }) {
  if (history.length === 0) {
    return (
      <div className="p-5 space-y-5">
        <p className="text-sm text-slate-400 text-center select-none">
          Results will appear here as the study runs.
        </p>
        <MethodologyCard />
      </div>
    );
  }

  // Separate pathway-specific results from pathway-independent ones (comparisons).
  const pathwayItems = history.filter(h => !PATHWAY_INDEPENDENT_TYPES.has(h.actionType));
  const independentItems = history.filter(h => PATHWAY_INDEPENDENT_TYPES.has(h.actionType));

  // Group pathway items by pathway name.
  // Extract pathway name from label: "LCA — Green H₂ — Houston" → "Green H₂ — Houston",
  // or fallback to "Pathway" for items without a dash separator.
  const groups = new Map(); // pathway name → [item]
  for (const item of pathwayItems) {
    const dashIdx = item.label.indexOf(' — ');
    const pathwayName = dashIdx !== -1 ? item.label.slice(dashIdx + 3) : item.label;
    if (!groups.has(pathwayName)) groups.set(pathwayName, []);
    groups.get(pathwayName).push(item);
  }

  return (
    <div className="p-5 space-y-5">
      {/* Pathway groups */}
      {[...groups.entries()].map(([pathwayName, items]) => {
        const pathwayId = items.find(i => i.data?.pathway_id)?.data?.pathway_id;
        const builderUrl = projectId && pathwayId
          ? `/projects/${projectId}/analysis/pathways/${pathwayId}`
          : null;

        return (
          <div key={pathwayName}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-xs font-semibold text-slate-600 truncate border-l-2 border-accent pl-2">{pathwayName}</div>
              {builderUrl && (
                <Link
                  to={builderUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-slate-400 hover:text-slate-600 shrink-0 ml-2"
                  title="Open pathway analysis in Builder"
                >
                  Analysis ↗
                </Link>
              )}
            </div>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              {items.map(item => (
                <ResultRow key={item.id} item={item} tabs={tabs} projectId={projectId} />
              ))}
            </div>
          </div>
        );
      })}

      {/* Comparisons and other pathway-independent results */}
      {independentItems.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-slate-600 mb-1.5 border-l-2 border-accent pl-2">Comparisons</div>
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            {independentItems.map(item => (
              <ResultRow key={item.id} item={item} tabs={tabs} projectId={projectId} />
            ))}
          </div>
        </div>
      )}

      <MethodologyCard />
    </div>
  );
}

IndexView.propTypes = {
  history: PropTypes.array.isRequired,
  tabs: PropTypes.array.isRequired,
  projectId: PropTypes.string,
};

// ---------------------------------------------------------------------------
// Tab bar
// ---------------------------------------------------------------------------

// Special sentinel values for the two permanent tabs.
const TAB_INDEX = null;
const TAB_ASSUMPTIONS = '__assumptions__';

function TabBar({ history, tabs, activeTab, onTabChange, hasAssumptions }) {
  const allTabs = [
    { id: TAB_INDEX, label: 'Index', closable: false },
    ...(hasAssumptions ? [{ id: TAB_ASSUMPTIONS, label: 'Assumptions', closable: false }] : []),
    ...tabs.map(id => {
      const item = history.find(h => h.id === id);
      return item
        ? { id, label: item.label, closable: true }
        : null;
    }).filter(Boolean),
  ];

  return (
    <div className="flex items-stretch border-b border-stone-200 bg-white overflow-x-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none] [-ms-overflow-style:none] shrink-0">
      {allTabs.map(tab => {
        const isActive = activeTab === tab.id;
        return (
          <button
            key={String(tab.id)}
            className={`
              relative flex items-center gap-1 px-3 py-2 text-xs whitespace-nowrap
              cursor-pointer select-none transition-colors shrink-0
              ${isActive ? 'text-slate-800 font-medium' : 'text-slate-400 hover:text-slate-700'}
            `}
            onClick={() => onTabChange(tab.id)}
          >
            <span className="max-w-40 truncate">{tab.label}</span>
            {tab.closable && (
              <span
                role="button"
                className="ml-1 text-slate-300 hover:text-slate-600 transition-colors"
                onClick={e => {
                  e.stopPropagation();
                  analystActions.closeTab(tab.id);
                }}
                title="Close tab"
              >
                ×
              </span>
            )}
            {isActive && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-slate-800 rounded-t-sm" />
            )}
          </button>
        );
      })}
    </div>
  );
}

TabBar.propTypes = {
  history: PropTypes.array.isRequired,
  tabs: PropTypes.array.isRequired,
  activeTab: PropTypes.any,
  onTabChange: PropTypes.func.isRequired,
  hasAssumptions: PropTypes.bool,
};

// ---------------------------------------------------------------------------
// Result card dispatcher
// ---------------------------------------------------------------------------

function ResultView({ item }) {
  if (!item) return null;
  switch (item.actionType) {
    case 'get_lca_results':
      return <LCAResultCard data={item.data} compact={false} />;
    case 'get_tea_results':
      return <TEAResultCard data={item.data} compact={false} />;
    case 'get_comparison_results':
      return <ComparisonResultCard data={item.data} compact={false} />;
    case 'get_sensitivity_results':
      return <SensitivityResultCard data={item.data} compact={false} />;
    case 'run_sensitivity_sweep':
      return <SweepResultCard data={item.data} />;
    case 'create_workspace_content':
      return <CustomContentCard data={item.data} />;
    default:
      return (
        <div className="text-sm text-slate-400 p-4">
          No renderer for {item.actionType}.
        </div>
      );
  }
}

ResultView.propTypes = {
  item: PropTypes.shape({
    actionType: PropTypes.string,
    data: PropTypes.object,
  }),
};

// ---------------------------------------------------------------------------
// Assumptions view
// ---------------------------------------------------------------------------

function PlanHeader({ review }) {
  if (!review) return null;
  const { narrative, location, scale, pathway_names, comparison } = review;
  return (
    <div className="shrink-0 px-5 py-4 border-b border-stone-100 bg-white space-y-2">
      {/* Pathway names */}
      {pathway_names?.length > 0 && (
        <div className="text-sm font-semibold text-slate-800">
          {pathway_names.join(' vs. ')}
        </div>
      )}
      {/* Meta row — simple pills, no emoji */}
      <div className="flex flex-wrap gap-1.5">
        {location && (
          <span className="text-xs bg-stone-100 text-slate-600 px-2 py-0.5 rounded-full">{location}</span>
        )}
        {scale && (
          <span className="text-xs bg-stone-100 text-slate-600 px-2 py-0.5 rounded-full">{scale}</span>
        )}
        <span className="text-xs bg-stone-100 text-slate-600 px-2 py-0.5 rounded-full">LCA</span>
        <span className="text-xs bg-stone-100 text-slate-600 px-2 py-0.5 rounded-full">TEA</span>
        {comparison && (
          <span className="text-xs bg-stone-100 text-slate-600 px-2 py-0.5 rounded-full">Comparison</span>
        )}
      </div>
      {/* Narrative */}
      {narrative && (
        <p className="text-sm text-slate-600 leading-relaxed">{narrative}</p>
      )}
    </div>
  );
}

PlanHeader.propTypes = {
  review: PropTypes.object,
};

// Rendered for a pathway that has a topology diagram but assumptions haven't
// been loaded yet (i.e. before propose_analysis fires).
function PathwayLoadingSlot({ name, topology }) {
  return (
    <div className="p-5 space-y-3">
      <div className="text-sm font-semibold text-slate-700">{name}</div>
      {topology && <PathwayDiagram topology={topology} />}
      <div className="flex items-center gap-2 text-xs text-slate-400">
        <span className="loading loading-dots loading-xs" />
        <span>Reviewing assumptions…</span>
      </div>
    </div>
  );
}

PathwayLoadingSlot.propTypes = {
  name: PropTypes.string.isRequired,
  topology: PropTypes.object,
};

// slots: [{caseId, name, topology, assumptions}] — unified ordered list of all
// pathways known to the workspace (may be partially populated during setup).
function AssumptionsView({ slots, pendingReview, review, onEditNode, onEditParam, disabled, collapsedSet, setCollapsedSet, projectId }) {
  const allCollapsed = slots.length > 0 && collapsedSet.size === slots.length;
  const toggleAll = () => {
    if (allCollapsed) {
      setCollapsedSet(new Set());
    } else {
      setCollapsedSet(new Set(slots.map(s => s.caseId)));
    }
  };
  const toggleOne = caseId => {
    setCollapsedSet(prev => {
      const next = new Set(prev);
      if (next.has(caseId)) next.delete(caseId);
      else next.add(caseId);
      return next;
    });
  };

  if (slots.length === 0) {
    return (
      <div className="flex flex-col h-full">
        {review && <PlanHeader review={review} />}
        <div className="flex-1 flex flex-col items-center justify-center text-center px-8 select-none">
          <p className="text-sm text-slate-400">
            Assumptions will appear here after the study runs.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {review && <PlanHeader review={review} />}

      {pendingReview && (
        <div className="shrink-0 px-5 py-3 border-b border-stone-100 bg-stone-50 flex items-center gap-3">
          <button
            className="btn btn-primary btn-sm"
            onClick={() => analystActions.sendMessage({ message: 'Run analysis' })}
          >
            Run analysis
          </button>
          <span className="text-xs text-slate-400">
            or ask me to change anything via chat
          </span>
        </div>
      )}

      {disabled && (
        <div className="shrink-0 px-5 py-2 border-b border-stone-100 bg-stone-50 flex items-center gap-2">
          <span className="loading loading-dots loading-xs text-slate-300" />
          <span className="text-xs text-slate-400">Analysis running…</span>
        </div>
      )}

      {/* Collapse/Expand all toggle — only when at least one full card is visible */}
      {slots.some(s => s.assumptions) && (
        <div className="shrink-0 px-5 pt-3 pb-1 flex justify-end">
          <button
            className="text-xs text-slate-400 hover:text-slate-600 select-none"
            onClick={toggleAll}
          >
            {allCollapsed ? 'Expand all' : 'Collapse all'}
          </button>
        </div>
      )}

      <div className={`flex-1 overflow-y-auto divide-y divide-stone-200 ${disabled ? 'opacity-60 pointer-events-none' : ''}`}>
        {slots.map(slot => {
          if (slot.assumptions) {
            const pathwayId = slot.assumptions?.pathway_id;
            const bUrl = projectId && pathwayId
              ? `/projects/${projectId}/build/pathways/${pathwayId}/${slot.caseId}`
              : null;
            return (
              <AssumptionsCard
                key={slot.caseId}
                data={slot.assumptions}
                collapsed={collapsedSet.has(slot.caseId)}
                onToggle={() => toggleOne(slot.caseId)}
                onEditNode={nodeId => onEditNode?.(slot.caseId, nodeId)}
                onEditParam={(nodeId, paramName) => onEditParam?.(slot.caseId, nodeId, paramName)}
                topologySlot={slot.topology ? <PathwayDiagram topology={slot.topology} /> : null}
                builderUrl={bUrl}
              />
            );
          }
          return (
            <PathwayLoadingSlot
              key={slot.caseId}
              name={slot.name}
              topology={slot.topology}
            />
          );
        })}
      </div>
    </div>
  );
}

AssumptionsView.propTypes = {
  slots: PropTypes.array.isRequired,
  pendingReview: PropTypes.bool,
  review: PropTypes.object,
  onEditNode: PropTypes.func,
  onEditParam: PropTypes.func,
  disabled: PropTypes.bool,
  collapsedSet: PropTypes.instanceOf(Set).isRequired,
  setCollapsedSet: PropTypes.func.isRequired,
  projectId: PropTypes.string,
};

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function WorkspaceEmpty() {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-8 select-none bg-white">
      <p className="text-sm text-slate-400 max-w-xs leading-relaxed">
        Results will appear here as the study runs.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

function WorkspacePanel() {
  const {
    workspaceStatus,
    workspaceReview,
    workspaceHistory,
    workspaceTabs,
    workspaceActiveTab,
    workspaceAssumptions,
    workspaceTopologies,
    workspaceCaseIdToName,
    workspaceResultsStale,
    projectId,
    isStreaming,
  } = useAnalystStore();

  const [editingNode, setEditingNode] = useState(null);
  const [collapsedSet, setCollapsedSet] = useState(new Set());

  // Unified ordered list of pathway slots — populated progressively as the
  // agent creates pathways and fetches topologies, before propose_analysis fires.
  const pathwaySlots = useMemo(() => {
    const seen = new Set();
    const slots = [];
    // Iterate caseIdToName first to preserve creation order.
    for (const [caseId, name] of Object.entries(workspaceCaseIdToName ?? {})) {
      seen.add(caseId);
      slots.push({
        caseId,
        name,
        topology: workspaceTopologies?.[caseId] ?? null,
        assumptions: workspaceAssumptions?.[caseId] ?? null,
      });
    }
    // Pick up any topology or assumption entries not yet in caseIdToName.
    for (const caseId of [
      ...Object.keys(workspaceTopologies ?? {}),
      ...Object.keys(workspaceAssumptions ?? {}),
    ]) {
      if (!seen.has(caseId)) {
        seen.add(caseId);
        slots.push({
          caseId,
          name: workspaceAssumptions?.[caseId]?.pathway_name ?? 'Pathway',
          topology: workspaceTopologies?.[caseId] ?? null,
          assumptions: workspaceAssumptions?.[caseId] ?? null,
        });
      }
    }
    return slots;
  }, [workspaceCaseIdToName, workspaceTopologies, workspaceAssumptions]);

  if (workspaceStatus === 'empty') return <WorkspaceEmpty />;

  // Results state: tab bar + content
  const activeItem = workspaceHistory.find(h => h.id === workspaceActiveTab) ?? null;
  // Show the Assumptions tab as soon as we have any pathway data — topology or
  // full assumptions — not just after propose_analysis fires.
  const hasAssumptions = pathwaySlots.length > 0;

  const handleEditNode = (caseId, nodeId, targetParam) => {
    const pathwayId =
      workspaceAssumptions[caseId]?.pathway_id ??
      workspaceHistory.find(h => h.data?.case_id === caseId && h.data?.pathway_id)?.data?.pathway_id;
    if (!pathwayId) return;
    setEditingNode({ caseId, nodeId, pathwayId, targetParam: targetParam ?? null });
  };

  const handleCloseEditor = () => {
    const { caseId } = editingNode;
    setEditingNode(null);
    analystActions.refreshAssumptions(caseId);
  };

  const isResultTab = workspaceActiveTab !== TAB_ASSUMPTIONS && workspaceActiveTab !== TAB_INDEX;

  return (
    <div className="relative flex flex-col h-full overflow-hidden bg-white">
      <TabBar
        history={workspaceHistory}
        tabs={workspaceTabs}
        activeTab={workspaceActiveTab}
        onTabChange={analystActions.setActiveTab}
        hasAssumptions={hasAssumptions}
      />

      {/* Stale results banner — shown on result tabs when params were edited */}
      {workspaceResultsStale && isResultTab && (
        <div className="shrink-0 px-4 py-2 bg-amber-50 border-b border-amber-100 flex items-center justify-between gap-3">
          <span className="text-xs text-amber-700">
            Parameters changed since last analysis — these results may be outdated.
          </span>
          <button
            className="text-xs text-amber-700 font-medium hover:text-amber-900 underline underline-offset-2 whitespace-nowrap"
            onClick={() => analystActions.sendMessage({ message: 'Re-run the analysis with the updated parameters.' })}
          >
            Re-run →
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto bg-white">
        {workspaceActiveTab === TAB_ASSUMPTIONS ? (
          <AssumptionsView
            slots={pathwaySlots}
            pendingReview={workspaceReview !== null && workspaceHistory.length === 0 && !isStreaming}
            review={workspaceReview}
            onEditNode={(caseId, nodeId) => handleEditNode(caseId, nodeId)}
            onEditParam={(caseId, nodeId, paramName) => handleEditNode(caseId, nodeId, paramName)}
            disabled={false}
            collapsedSet={collapsedSet}
            setCollapsedSet={setCollapsedSet}
            projectId={projectId}
          />
        ) : workspaceActiveTab === TAB_INDEX ? (
          <IndexView history={workspaceHistory} tabs={workspaceTabs} projectId={projectId} />
        ) : (
          <div className="p-5">
            <ResultView item={activeItem} />
          </div>
        )}
      </div>

      {/* Footer */}
      {projectId && (
        <div className="shrink-0 px-5 py-3 border-t border-stone-100 flex justify-end">
          <Link
            to={`/projects/${projectId}`}
            className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
            target="_blank"
            rel="noopener noreferrer"
          >
            Open project in builder view ↗
          </Link>
        </div>
      )}

      {/* Node param editor slide panel */}
      <div
        className={`absolute inset-0 bg-white z-20 flex flex-col transform transition-transform duration-300 ease-in-out ${
          editingNode ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {editingNode && (
          <NodeParamEditor
            pathwayId={editingNode.pathwayId}
            caseId={editingNode.caseId}
            nodeId={editingNode.nodeId}
            targetParam={editingNode.targetParam}
            onClose={handleCloseEditor}
          />
        )}
      </div>
    </div>
  );
}

export default WorkspacePanel;
